"use client"

import React, { useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { format } from 'date-fns'
import { CalendarIcon, ChevronLeft, ChevronRight, FileText, Plus, Trash2, ZoomIn } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Calendar } from '@/shared/components/ui/calendar'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'
import { useIsIOS } from '@/shared/hooks/use-is-ios'
import errorToast from '@/shared/lib/toast/error.toast'
import successToast from '@/shared/lib/toast/success.toast'
import { pdfUrlToThumbnailDataUrl } from '@/shared/lib/pdf-thumbnail'
import type { BasePhotoMetadata } from '../types/base-photo-metadata'
import { deleteBasePhoto } from '@/features/base/api/base-photos.api'
import { authFetch, authFetchJson } from '@/shared/lib/auth-fetch'

interface ReportsPhotosCarouselProps {
  regionId: number | null
  componentName: string
  canEdit: boolean
}

function toDateString(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d
  return format(date, 'yyyy-MM-dd')
}

function toDisplayDate(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d
  return format(date, 'MMM d, yyyy')
}

export function ReportsPhotosCarousel({
  regionId,
  componentName,
  canEdit,
}: ReportsPhotosCarouselProps) {
  const [list, setList] = useState<BasePhotoMetadata[]>([])
  const [loading, setLoading] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [urlCache, setUrlCache] = useState<Map<number, { imageUrl: string; thumbnailUrl: string }>>(new Map())
  const [loadingUrl, setLoadingUrl] = useState<Set<number>>(new Set())
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadCreatedAt, setUploadCreatedAt] = useState<string>(() => toDateString(new Date()))
  const [uploading, setUploading] = useState(false)
  const [uploadDatePickerOpen, setUploadDatePickerOpen] = useState(false)
  const [editDatePhotoId, setEditDatePhotoId] = useState<number | null>(null)
  const [editDateValue, setEditDateValue] = useState<Date | undefined>(undefined)
  const [zoomOpen, setZoomOpen] = useState(false)
  const [pdfThumbnailCache, setPdfThumbnailCache] = useState<Map<number, string>>(new Map())
  const [loadingPdfThumbnail, setLoadingPdfThumbnail] = useState<Set<number>>(new Set())
  const [failedPdfThumbnail, setFailedPdfThumbnail] = useState<Set<number>>(new Set())
  const [failedSignedThumbnail, setFailedSignedThumbnail] = useState<Set<number>>(new Set())
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isIOS = useIsIOS()

  const fetchMetadata = useCallback(async () => {
    if (!regionId || !componentName?.trim()) {
      setList([])
      return
    }
    setLoading(true)
    try {
      const res = await authFetchJson<BasePhotoMetadata[]>(
        `/base-photos/region/${regionId}/metadata?componentName=${encodeURIComponent(componentName)}`
      )
      if (!res.success) {
        throw new Error(res.message || 'Failed to load metadata')
      }
      const photos = res.data
      // Newest first so the most recent upload shows first; scrolling Next goes from newest to oldest
      const sorted = [...photos].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      setList(sorted)
      setCurrentIndex((i) => (sorted.length > 0 ? Math.min(i, sorted.length - 1) : 0))
    } catch (e) {
      console.error('ReportsPhotosCarousel fetchMetadata:', e)
      setList([])
    } finally {
      setLoading(false)
    }
  }, [regionId, componentName])

  useEffect(() => {
    fetchMetadata()
  }, [fetchMetadata])

  // Close Add Photo dialog if user loses edit permission (e.g. role change)
  useEffect(() => {
    if (!canEdit) setUploadOpen(false)
  }, [canEdit])

  const fetchSignedUrl = useCallback(async (id: number) => {
    if (urlCache.has(id)) return
    setLoadingUrl((s) => new Set(s).add(id))
    try {
      const res = await authFetchJson<any>(`/base-photos/${id}/signed-url`)
      if (!res.success) {
        throw new Error(res.message || 'Failed to fetch signed URL')
      }
      const urls = res.data
      if (urls?.imageUrl && urls?.thumbnailUrl) {
        setUrlCache((m) => new Map(m).set(id, { imageUrl: urls.imageUrl, thumbnailUrl: urls.thumbnailUrl }))
      }
    } catch (e) {
      console.error('ReportsPhotosCarousel fetchSignedUrl:', e)
    } finally {
      setLoadingUrl((s) => {
        const n = new Set(s)
        n.delete(id)
        return n
      })
    }
  }, [urlCache])

  const current = list[currentIndex] ?? null
  useEffect(() => {
    if (current?.id) fetchSignedUrl(current.id)
  }, [current?.id, fetchSignedUrl])

  // Generate PDF thumbnail (first page) for Job Board so PDFs show a preview instead of generic icon
  useEffect(() => {
    if (!current?.id || !current.imageUrl?.toLowerCase().includes('.pdf')) return
    const cached = pdfThumbnailCache.get(current.id)
    if (cached || loadingPdfThumbnail.has(current.id) || failedPdfThumbnail.has(current.id)) return
    const urls = urlCache.get(current.id)
    if (!urls?.imageUrl) return
    // Prefer backend-generated thumbnail when available unless it already failed to load.
    if (urls.thumbnailUrl && !failedSignedThumbnail.has(current.id)) return

    setLoadingPdfThumbnail((s) => new Set(s).add(current.id))
    // Use signed image URL for role-safe client thumbnail generation.
    // TeamLeader/Volunteer do not have access to the admin-only download proxy.
    const pdfUrl = urls.imageUrl
    pdfUrlToThumbnailDataUrl(pdfUrl)
      .then((dataUrl) => {
        setPdfThumbnailCache((m) => new Map(m).set(current.id, dataUrl))
      })
      .catch(() => {
        // CORS or role-based access failure: do not retry forever.
        setFailedPdfThumbnail((s) => new Set(s).add(current.id!))
      })
      .finally(() => {
        setLoadingPdfThumbnail((s) => {
          const n = new Set(s)
          n.delete(current.id)
          return n
        })
      })
  }, [current?.id, current?.imageUrl, urlCache, pdfThumbnailCache, loadingPdfThumbnail, failedPdfThumbnail, failedSignedThumbnail])

  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1))
  const goNext = () =>
    setCurrentIndex((i) => (i >= list.length - 1 ? 0 : i + 1))

  const handleUpload = async () => {
    if (!canEdit || !regionId || !uploadFile) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('photo', uploadFile)
      form.append('regionId', String(regionId))
      form.append('componentName', componentName)
      form.append('createdAt', uploadCreatedAt)
      const res = await authFetch('/base-photos/upload', { method: 'POST', body: form })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.success === false) {
        errorToast(data?.error || data?.message || 'Upload failed')
        return
      }
      successToast('Photo uploaded')
      setUploadOpen(false)
      setUploadFile(null)
      setUploadCreatedAt(toDateString(new Date()))
      await fetchMetadata()
    } catch (e) {
      console.error('ReportsPhotosCarousel handleUpload:', e)
      errorToast(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!canEdit) return
    if (!window.confirm('Delete this photo permanently? It will be removed from S3 and the database.')) return
    try {
      const result = await deleteBasePhoto(id)
      if (!result.success) {
        errorToast(result.message || 'Delete failed')
        return
      }
      successToast('Photo deleted')
      setList((prev) => prev.filter((p) => p.id !== id))
      setUrlCache((m) => {
        const n = new Map(m)
        n.delete(id)
        return n
      })
      setPdfThumbnailCache((m) => {
        const n = new Map(m)
        n.delete(id)
        return n
      })
      setCurrentIndex((i) => Math.max(0, Math.min(i, list.length - 2)))
    } catch (e) {
      console.error('ReportsPhotosCarousel handleDelete:', e)
      errorToast(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  const openEditDate = (photo: BasePhotoMetadata) => {
    setEditDatePhotoId(photo.id)
    setEditDateValue(new Date(photo.createdAt))
  }

  const handleSaveEditDate = async () => {
    if (!canEdit) return
    if (editDatePhotoId == null || editDateValue == null) {
      setEditDatePhotoId(null)
      return
    }
    try {
      const res = await authFetch(`/base-photos/${editDatePhotoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ createdAt: editDateValue.toISOString() }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.success === false) {
        errorToast(data?.error || 'Update failed')
        return
      }
      successToast('Date updated')
      setList((prev) => {
        const updated = prev.map((p) =>
          p.id === editDatePhotoId ? { ...p, createdAt: editDateValue.toISOString() } : p
        )
        return [...updated].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      })
      setEditDatePhotoId(null)
      setEditDateValue(undefined)
    } catch (e) {
      console.error('ReportsPhotosCarousel handleSaveEditDate:', e)
      errorToast(e instanceof Error ? e.message : 'Update failed')
    }
  }

  if (!regionId) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        Select a region to view photos.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="ml-2">Loading photos…</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end print:hidden">
          <Button
            size="sm"
            onClick={() => setUploadOpen(true)}
            className="flex items-center gap-2"
            title="Upload"
            aria-label="Upload"
          >
            <Plus className="h-4 w-4" />
            <span className="max-sm:hidden">Upload</span>
          </Button>
        </div>
      )}

      {list.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          {canEdit ? 'No photos yet. Click "Add Photo" to upload.' : 'No photos for this section.'}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="relative flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0 print:hidden"
              onClick={goPrev}
              disabled={currentIndex <= 0}
              aria-label="Previous"
              showLoader={false}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>

            <div className="relative flex-1 flex justify-center items-center min-h-[200px] rounded-lg border bg-muted/30 overflow-hidden print:bg-white print:overflow-visible print:min-h-0">
              {current && (
                <>
                  {loadingUrl.has(current.id) ? (
                    <div className="flex items-center justify-center w-full h-full">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                    </div>
                  ) : urlCache.get(current.id) ? (
                    (() => {
                      const url = urlCache.get(current.id)!.imageUrl
                      const isPdf = current.imageUrl?.toLowerCase().includes('.pdf')
                      const openZoom = () => setZoomOpen(true)
                      const signedThumbnail = isPdf ? urlCache.get(current.id)?.thumbnailUrl : null
                      const pdfThumbnail = isPdf ? pdfThumbnailCache.get(current.id) : null
                      const pdfThumbLoading = isPdf && loadingPdfThumbnail.has(current.id)
                      if (isPdf) {
                        return (
                          <>
                            {signedThumbnail && !failedSignedThumbnail.has(current.id) ? (
                              <>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                  src={signedThumbnail}
                                  alt="PDF preview"
                                  className="max-h-[600px] w-auto max-w-full object-contain print:hidden"
                                  onError={() =>
                                    setFailedSignedThumbnail((s) => new Set(s).add(current.id))
                                  }
                                />
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                  src={signedThumbnail}
                                  alt="PDF preview"
                                  className="reports-jobboard-print-img absolute inset-0 max-h-[600px] w-auto max-w-full object-contain opacity-0 pointer-events-none -z-10 print:opacity-100 print:relative print:z-auto print:max-w-full print:block"
                                  onError={() =>
                                    setFailedSignedThumbnail((s) => new Set(s).add(current.id))
                                  }
                                />
                              </>
                            ) : pdfThumbnail ? (
                              <>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                  src={pdfThumbnail}
                                  alt="PDF preview"
                                  className="max-h-[600px] w-auto max-w-full object-contain print:hidden"
                                />
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                  src={pdfThumbnail}
                                  alt="PDF preview"
                                  className="reports-jobboard-print-img absolute inset-0 max-h-[600px] w-auto max-w-full object-contain opacity-0 pointer-events-none -z-10 print:opacity-100 print:relative print:z-auto print:max-w-full print:block"
                                />
                              </>
                            ) : pdfThumbLoading ? (
                              <div className="flex items-center justify-center w-full h-full text-muted-foreground text-sm">
                                Generating thumbnail…
                              </div>
                            ) : (
                              <>
                                <iframe
                                  src={`${url}#toolbar=0&navpanes=0`}
                                  title="PDF"
                                  className="absolute inset-0 w-full h-full min-h-[340px] border-0 pointer-events-none print:relative print:min-h-[400px]"
                                />
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted/50 print:hidden">
                                  <FileText className="h-12 w-12 text-muted-foreground" />
                                  <span className="text-sm text-muted-foreground">PDF</span>
                                </div>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={openZoom}
                              className="absolute inset-0 z-[1] cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset print:hidden"
                              aria-label="Click to zoom"
                              title="Click to zoom"
                            />
                            <Button
                              type="button"
                              variant="default"
                              size="icon"
                              className="absolute left-3 bottom-3 z-10 h-10 w-10 shadow-lg print:hidden"
                              onClick={openZoom}
                              aria-label="Zoom in to view"
                              title="Click to zoom"
                            >
                              <ZoomIn className="h-5 w-5" />
                            </Button>
                          </>
                        )
                      }
                      return (
                        <>
                          <Image
                            src={url}
                            alt={current.description || 'Report photo'}
                            width={800}
                            height={600}
                            className="max-h-[600px] w-auto max-w-full object-contain print:hidden"
                            unoptimized
                          />
                          {/* Plain img for print: visible only when printing. Kept in DOM and visible on screen (opacity-0) so the browser loads it; otherwise it often doesn't appear in print. */}
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={url}
                            alt={current.description || 'Report photo'}
                            className="reports-jobboard-print-img absolute inset-0 max-h-[600px] w-auto max-w-full object-contain opacity-0 pointer-events-none -z-10 print:opacity-100 print:relative print:z-auto print:max-w-full print:block"
                          />
                          <button
                            type="button"
                            onClick={openZoom}
                            className="absolute inset-0 z-[1] flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset print:hidden"
                            aria-label="Click to zoom"
                            title="Click to zoom"
                          />
                          <Button
                            type="button"
                            variant="default"
                            size="icon"
                            className="absolute left-3 bottom-3 z-10 h-10 w-10 shadow-lg print:hidden"
                            onClick={(e) => {
                              e.stopPropagation()
                              openZoom()
                            }}
                            aria-label="Zoom in to view"
                            title="Click to zoom"
                          >
                            <ZoomIn className="h-5 w-5" />
                          </Button>
                        </>
                      )
                    })()
                  ) : null}
                </>
              )}
            </div>

            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0 print:hidden"
              onClick={goNext}
              disabled={list.length <= 1}
              aria-label="Next"
              showLoader={false}
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {current && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-8 items-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground">
                  {toDisplayDate(current.createdAt)}
                </span>
                {current.description && (
                  <span className="text-muted-foreground">· {current.description}</span>
                )}
                {canEdit && (
                  <>
                    {isIOS ? (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 print:hidden"
                          onClick={() => openEditDate(current)}
                        >
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          Edit date
                        </Button>
                        <Dialog
                          open={editDatePhotoId === current.id}
                          onOpenChange={(open) => {
                            if (!open) {
                              setEditDatePhotoId(null)
                              setEditDateValue(undefined)
                            }
                          }}
                        >
                          <DialogContent className="sm:max-w-[300px]">
                            <DialogHeader>
                              <DialogTitle>Edit date</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3 py-4">
                              <input
                                type="date"
                                value={editDateValue ? format(editDateValue, 'yyyy-MM-dd') : ''}
                                onChange={(e) => {
                                  const val = e.target.value
                                  if (val) {
                                    const [y, m, d] = val.split('-').map(Number)
                                    setEditDateValue(new Date(y, m - 1, d))
                                  }
                                }}
                                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm"
                              />
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setEditDatePhotoId(null)
                                    setEditDateValue(undefined)
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button size="sm" onClick={handleSaveEditDate}>
                                  Save
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </>
                    ) : (
                    <Popover
                      open={editDatePhotoId === current.id}
                      onOpenChange={(open) => {
                        if (!open) {
                          setEditDatePhotoId(null)
                          setEditDateValue(undefined)
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 print:hidden"
                          onClick={() => openEditDate(current)}
                        >
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          Edit date
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={editDateValue}
                          onSelect={(d) => setEditDateValue(d)}
                          initialFocus
                        />
                        <div className="p-2 border-t flex justify-end">
                          <Button size="sm" onClick={handleSaveEditDate}>
                            Save
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-destructive hover:text-destructive print:hidden"
                      onClick={() => handleDelete(current.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </>
                )}
              </div>
              <span className="text-muted-foreground">
                {currentIndex + 1} / {list.length}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Zoom lightbox: click image or Zoom button to view larger */}
      {current && urlCache.get(current.id) && (
        <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
          <DialogContent
            className="max-w-[95vw] w-full max-h-[90vh] overflow-auto p-2 sm:p-4"
            hideCloseButton={false}
          >
            <DialogTitle className="sr-only">View zoomed</DialogTitle>
            {(() => {
              const url = urlCache.get(current!.id)!.imageUrl
              const isPdf = current!.imageUrl?.toLowerCase().includes('.pdf')
              if (isPdf) {
                return (
                  <iframe
                    src={`${url}#navpanes=0`}
                    title="PDF (zoomed)"
                    className="min-h-[75vh] h-[80vh] w-full border-0"
                  />
                )
              }
              return (
                <Image
                  src={url}
                  alt={current!.description || 'Report photo'}
                  width={1200}
                  height={900}
                  className="max-h-[85vh] w-full max-w-full object-contain"
                  unoptimized
                />
              )
            })()}
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={uploadOpen && canEdit}
        onOpenChange={(open) => {
          setUploadOpen(open)
          if (!open) setUploadDatePickerOpen(false)
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Add Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Photo or PDF</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  autoFocus
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose file
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                />
                <span className="text-sm text-muted-foreground">
                  {uploadFile ? uploadFile.name : 'No file chosen'}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date (editable later)</Label>
              {isIOS ? (
                <input
                  type="date"
                  value={uploadCreatedAt}
                  onChange={(e) => setUploadCreatedAt(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              ) : (
                <Popover open={uploadDatePickerOpen} onOpenChange={setUploadDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {uploadCreatedAt ? format(new Date(uploadCreatedAt), 'PPP') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={uploadCreatedAt ? new Date(uploadCreatedAt) : undefined}
                      onSelect={(d) => {
                        if (d) {
                          setUploadCreatedAt(toDateString(d))
                          setUploadDatePickerOpen(false)
                        }
                      }}
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!uploadFile || uploading} showLoader={false}>
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
